import { showSessionTimeout, showCompanyMismatchError, showServerError } from './toastHelper';



const { hostname } = window.location;
const isLocalHost = (h) => h === 'localhost' || h === '127.0.0.1' || h === '::1';

export const getEnv = () => {
  // Check if environment is manually set in localStorage
  const manualEnv = localStorage.getItem('api_environment');
  if (manualEnv === 'test' || manualEnv === 'prod') {
    return manualEnv;
  }

  // Default environment detection
  return isLocalHost(hostname) || hostname.indexOf('test') === 0 ? 'test' : 'prod';
};

export const isTestEnv = () => getEnv() === 'test';

// Set environment manually (can be called from console for testing)
export const setEnvironment = (env) => {
  if (env === 'test' || env === 'prod') {
    localStorage.setItem('api_environment', env);
    window.location.reload(); // Reload to apply changes
  } else {
    console.error('Invalid environment. Use "test" or "prod"');
  }
};

// Reset to automatic environment detection
export const resetEnvironment = () => {
  localStorage.removeItem('api_environment');
  window.location.reload(); // Reload to apply changes
};

const PROD_FALLBACK_API_URL = 'https://subsbackend.kito.pet';
const ENV_API_URL = process.env.REACT_APP_API_URL;

// Fail-fast in local/test so we never silently hit the wrong backend.
if ((isLocalHost(hostname) || hostname.indexOf('test') === 0) && !ENV_API_URL) {
  throw new Error(
    'REACT_APP_API_URL is required in local/test. ' +
    'Set it to your backend URL (e.g. http://localhost:8000) and restart the dev server.'
  );
}

let prod_api_url = ENV_API_URL || PROD_FALLBACK_API_URL; // Production API URL
let local_api_url = ENV_API_URL; // Local/test API URL (explicit only)

const config = {
  baseUrl: isTestEnv() ? local_api_url : prod_api_url,
  prodUrl: prod_api_url,
  localUrl: local_api_url
};

export const resolveFromBaseURL = path => config.baseUrl + path;

const API_URL = config.baseUrl;

console.log('Current API URL:', API_URL, 'Environment:', getEnv());

class CacheManager {
  constructor() {
    this.cache = new Map();
    this.refreshThreshold = 30 * 1000; // 30 saniye
    this.defaultDuration = 5 * 60 * 1000; // 5 dakika
  }

  shouldCache(endpoint, method) {
    return method === 'GET' &&
      endpoint.startsWith('/products') &&
      !window.location.pathname.startsWith('/admin') &&
      !endpoint.includes('?since_id=');
  }

  get(key) {
    try {
      const item = this.cache.get(key) || JSON.parse(sessionStorage.getItem(`cache_${key}`));

      if (!item) return null;
      if (item.expiry < Date.now()) {
        this.remove(key);
        return null;
      }

      return item.value;
    } catch {
      return null;
    }
  }

  set(key, value) {
    const item = {
      value,
      timestamp: Date.now(),
      expiry: Date.now() + this.defaultDuration
    };

    this.cache.set(key, item);
    try {
      sessionStorage.setItem(`cache_${key}`, JSON.stringify(item));
    } catch (error) {
      console.warn('Cache storage failed:', error);
    }
  }

  remove(key) {
    this.cache.delete(key);
    sessionStorage.removeItem(`cache_${key}`);
  }

  clear() {
    this.cache.clear();
    Object.keys(sessionStorage)
      .filter(key => key.startsWith('cache_'))
      .forEach(key => sessionStorage.removeItem(key));
  }
}

const cacheManager = new CacheManager();

// Refresh token lock mekanizması - aynı anda sadece bir refresh token isteği yapılmasını sağlar
let refreshTokenPromise = null;
let isRefreshing = false;

// Auth gerektirmeyen endpointler
const PUBLIC_ENDPOINTS = [
  '/auth/login',
  '/auth/signup',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/verify-email',
  '/auth/resend-verification'
];

/**
 * Unified API request function that handles all types of requests
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Request options
 * @param {string} options.method - HTTP method (GET, POST, PUT, DELETE)
 * @param {Object|FormData} options.data - Request data
 * @param {boolean} options.useCache - Whether to use cache for GET requests
 * @param {boolean} options.skipAuth - Whether to skip adding auth header
 * @param {Object} options.headers - Additional headers
 * @returns {Promise<any>}
 */
export const apiRequest = async (endpoint, options = {}) => {
  const {
    method = 'GET',
    data,
    body,
    useCache = true,
    skipAuth = PUBLIC_ENDPOINTS.some(path => endpoint.startsWith(path)),
    headers = {}
  } = options;

  // Cache control
  if (useCache && method === 'GET' && cacheManager.shouldCache(endpoint, method)) {
    const cachedData = cacheManager.get(endpoint);
    if (cachedData && document.visibilityState !== 'visible') {
      return cachedData;
    }
  }

  // Request headers
  const token = localStorage.getItem('auth_token');
  const defaultHeaders = {
    ...(!(data instanceof FormData || body instanceof FormData) && { 'Content-Type': 'application/json' }),
    ...(!skipAuth && token && { 'Authorization': `Bearer ${token}` }),
    ...headers
  };

  // Request options
  const requestOptions = {
    method,
    headers: defaultHeaders,
    credentials: 'include', // CORS için gerekli
    redirect: 'follow',
    mode: 'cors' // CORS modunu açıkça belirt
  };

  // Add body if data or body exists (data takes precedence)
  const requestData = data || body;
  if (requestData) {
    if (requestData instanceof FormData) {
      requestOptions.body = requestData;
    } else if (typeof requestData === 'string') {
      requestOptions.body = requestData;
    } else {
      requestOptions.body = JSON.stringify(requestData);
    }
  }

  try {
    const requestUrl = `${API_URL}${endpoint}`;

    const response = await fetch(requestUrl, requestOptions);

    // OPTIONS isteklerini atla
    if (method === 'OPTIONS') {
      return;
    }

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('Invalid response:', text);
      throw new Error(`404 Not Found`);
    }

    // Auth check
    if (response.status === 401 && !skipAuth) {
      // Token yenileme denemesi
      const refreshToken = localStorage.getItem('auth_refresh_token');

      // Eğer refresh token varsa ve şu anki istek refresh isteği değilse
      if (refreshToken && !endpoint.includes('/auth/refresh-token')) {
        try {
          // Eğer zaten bir refresh token isteği devam ediyorsa, onu bekle
          if (isRefreshing && refreshTokenPromise) {
            console.log('Token refresh already in progress, waiting...');
            try {
              await refreshTokenPromise;
              // Refresh tamamlandı, yeni token ile orijinal isteği tekrarla
              const newToken = localStorage.getItem('auth_token');
              if (newToken) {
                return apiRequest(endpoint, {
                  ...options,
                  headers: {
                    ...headers,
                    'Authorization': `Bearer ${newToken}`
                  }
                });
              } else {
                // Refresh başarısız oldu, logout yap
                throw new Error('Token refresh failed');
              }
            } catch (error) {
              // Refresh başarısız oldu, bekleyen istek için de logout yap
              localStorage.removeItem('auth_token');
              localStorage.removeItem('auth_refresh_token');
              localStorage.removeItem('auth_user');
              const authError = new Error('Yetkisiz erişim');
              authError.isAuthError = true;
              showSessionTimeout();
              throw authError;
            }
          } else if (!isRefreshing) {
            // İlk refresh token isteği - lock'u ayarla
            isRefreshing = true;
            console.log('Token expired, attempting refresh...');

            refreshTokenPromise = (async () => {
              try {
                const refreshResponse = await fetch(`${API_URL}/auth/refresh-token`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({ refreshToken })
                });

                if (refreshResponse.ok) {
                  const contentType = refreshResponse.headers.get('content-type');
                  if (!contentType || !contentType.includes('application/json')) {
                    console.error('Token refresh response is not JSON');
                    throw new Error('Invalid response format');
                  }

                  const data = await refreshResponse.json();
                  // Schema: { success, accessToken, refreshToken }
                  // OpenAPI'ye göre success field'ı var, kontrol edelim
                  if (data.success !== false && data.accessToken) {
                    console.log('Token refresh successful');
                    localStorage.setItem('auth_token', data.accessToken);
                    if (data.refreshToken) {
                      localStorage.setItem('auth_refresh_token', data.refreshToken);
                    }
                    return data.accessToken;
                  } else {
                    console.error('Token refresh failed: invalid response', data);
                    throw new Error('Invalid refresh response');
                  }
                } else {
                  // Refresh token başarısız oldu, response body'yi oku
                  try {
                    const errorData = await refreshResponse.json();
                    console.error('Token refresh failed with status:', refreshResponse.status, errorData);
                  } catch (e) {
                    console.error('Token refresh failed with status:', refreshResponse.status);
                  }
                  throw new Error('Token refresh failed');
                }
              } finally {
                // Lock'u kaldır
                isRefreshing = false;
                refreshTokenPromise = null;
              }
            })();

            // Refresh token isteğini bekle
            const newToken = await refreshTokenPromise;

            if (newToken) {
              // Orijinal isteği yeni token ile tekrarla
              return apiRequest(endpoint, {
                ...options,
                headers: {
                  ...headers,
                  'Authorization': `Bearer ${newToken}`
                }
              });
            }
          }
        } catch (refreshError) {
          console.error('Token refresh error:', refreshError);
          // Lock'u temizle
          isRefreshing = false;
          refreshTokenPromise = null;
        }
      }

      // Refresh başarısız olduysa veya refresh token yoksa çıkış yap
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_refresh_token');
      localStorage.removeItem('auth_user');

      // AuthContext'i haberdar et
      window.dispatchEvent(new Event('auth-logout'));

      const error = new Error('Yetkisiz erişim');
      error.isAuthError = true; // Özel flag ekle
      showSessionTimeout();
      throw error;
    }

    // Company mismatch check (403 Forbidden)
    if (response.status === 403 && !skipAuth) {
      const errorData = await response.json().catch(() => null);
      const errorMessage = errorData?.message || 'Bu kaynak sizin şirketinize ait değil';

      // Check if error message indicates company mismatch
      if (errorMessage.includes('şirket') || errorMessage.includes('company') ||
        errorMessage.includes('uyuşmuyor') || errorMessage.includes('ait değil')) {
        showCompanyMismatchError(errorMessage);
      }

      const error = new Error(errorMessage);
      error.status = 403;
      error.isCompanyMismatch = true; // Özel flag ekle
      error.data = errorData;
      throw error;
    }

    // Error handling
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      const error = new Error(errorData?.message || `HTTP error! ${response.status}`);

      if (!errorData) {
        error.apiError = true;
        throw error;
      }

      error.status = response?.status;
      error.data = errorData;
      throw error;
    }

    const responseData = await response.json();

    // Cache successful GET responses
    if (useCache && method === 'GET' && cacheManager.shouldCache(endpoint, method)) {
      cacheManager.set(endpoint, responseData);
    }

    return responseData;
  } catch (error) {
    console.error('Request failed:', {
      url: `${API_URL}${endpoint}`,
      options: requestOptions,
      environment: getEnv(),
      error
    });

    // Network error veya sunucuya ulaşılamama durumu
    if (!(error instanceof Error) || !error.hasOwnProperty('status')) {
      error.apiError = true;
      // Sunucuya ulaşılamama durumunda direkt toast göster
      // (debounce mekanizması toastHelper içinde)
      if (error.apiError && !error.isAuthError && !error.isCompanyMismatch) {
        showServerError();
      }
    }

    throw error;
  }
};

// Export cache control functions
export const clearCache = () => cacheManager.clear();
export const clearEndpointCache = (endpoint) => cacheManager.remove(endpoint);

// Dashboard API Functions
export const getDashboardStatistics = {
  subscriptions: () => apiRequest('/dashboard/statistics/subscriptions'),
  deliveries: () => apiRequest('/dashboard/statistics/deliveries'),
  products: () => apiRequest('/dashboard/statistics/products'),
  customers: () => apiRequest('/dashboard/statistics/customers'),
  recentActivity: () => apiRequest('/dashboard/statistics/recent-activity'),
  charts: () => apiRequest('/dashboard/statistics/charts'),
  alerts: () => apiRequest('/dashboard/statistics/alerts')
};

// Order Export
export const exportOrders = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.status && filters.status !== 'all') params.append('status', filters.status);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  const qs = params.toString();

  const res = await apiRequest(`/orders/export${qs ? `?${qs}` : ''}`);
  if (!res?.success || !res.data) throw new Error('Export failed');

  // Convert JSON array to CSV
  const rows = res.data;
  if (rows.length === 0) throw new Error('Dışa aktarılacak veri yok');
  const headers = Object.keys(rows[0]);
  const csvContent = [
    headers.join(','),
    ...rows.map(r => headers.map(h => `"${(r[h] ?? '').toString().replace(/"/g, '""')}"`).join(','))
  ].join('\n');

  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `siparisler_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Usage examples:
/*
// Public endpoint (login)
apiRequest('/auth/login', {
  method: 'POST',
  data: { email: 'user@example.com', password: '123456' }
});

// Protected endpoint (requires auth)
apiRequest('/products', {
  method: 'POST',
  data: { name: 'Product' }
});

// File upload
const formData = new FormData();
formData.append('image', file);
apiRequest('/upload', {
  method: 'POST',
  data: formData
});

// GET request with cache
apiRequest('/products');

// GET request without cache
apiRequest('/products', { useCache: false });

// Force skip auth header
apiRequest('/public-endpoint', { skipAuth: true });
*/